import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { Loader2, Lock, Eye, EyeOff, KeyRound } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PROJECT_LOGO = "https://customer-assets.emergentagent.com/job_7952493e-ecb0-4483-a1aa-f19d77930300/artifacts/k61v6idi_Project.png";

const PasswordInput = ({ id, value, onChange, placeholder, show, onToggle, testId }) => (
  <div className="relative">
    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
    <Input
      id={id}
      type={show ? 'text' : 'password'}
      placeholder={placeholder}
      value={value}
      onChange={onChange}
      className="pl-10 pr-10"
      required
      data-testid={testId}
    />
    <button
      type="button"
      onClick={onToggle}
      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors"
      data-testid={`${testId}-toggle`}
      tabIndex={-1}
    >
      {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
    </button>
  </div>
);

const ChangePasswordPage = () => {
  const navigate = useNavigate();
  const { token, logout } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [form, setForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (form.newPassword.length < 6) {
      toast.error('New password must be at least 6 characters');
      return;
    }
    if (form.newPassword !== form.confirmPassword) {
      toast.error('New passwords do not match');
      return;
    }
    setIsLoading(true);
    try {
      await axios.post(`${API}/auth/change-password`, {
        current_password: form.currentPassword,
        new_password: form.newPassword,
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Password changed successfully! Please sign in again.');
      logout();
      navigate('/login');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to change password');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div data-testid="change-password-page" className="min-h-screen grid grid-cols-1 lg:grid-cols-2">
      {/* Left Panel */}
      <div className="hidden lg:flex flex-col justify-center items-center p-12 bg-slate-900 text-white relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900" />
        <div className="absolute top-0 right-0 w-96 h-96 bg-violet-500/10 rounded-full blur-3xl" />
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-violet-600/10 rounded-full blur-3xl" />
        <div className="relative z-10 text-center space-y-8">
          <img src={PROJECT_LOGO} alt="S1000D Project" className="w-48 h-48 object-contain mx-auto drop-shadow-2xl" />
          <h2 className="text-3xl font-bold" style={{ fontFamily: 'Outfit, sans-serif' }}>Set New Password</h2>
          <p className="text-slate-400 max-w-sm">Please create a new secure password for your account</p>
        </div>
      </div>

      {/* Right Panel */}
      <div className="flex items-center justify-center p-8 bg-slate-50">
        <div className="w-full max-w-md">
          <Card className="border-slate-200 shadow-xl">
            <CardHeader className="text-center pb-2">
              <div className="mx-auto w-16 h-16 bg-violet-50 rounded-full flex items-center justify-center mb-4">
                <KeyRound className="w-8 h-8 text-violet-600" />
              </div>
              <CardTitle className="text-2xl" style={{ fontFamily: 'Outfit, sans-serif' }}>Change Password</CardTitle>
              <CardDescription>You must set a new password to continue</CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-5">
                <div className="space-y-2">
                  <Label>Current / Temporary Password</Label>
                  <PasswordInput
                    id="currentPassword" value={form.currentPassword}
                    onChange={(e) => setForm({ ...form, currentPassword: e.target.value })}
                    placeholder="Enter current password" show={showCurrent}
                    onToggle={() => setShowCurrent(!showCurrent)} testId="current-password-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>New Password</Label>
                  <PasswordInput
                    id="newPassword" value={form.newPassword}
                    onChange={(e) => setForm({ ...form, newPassword: e.target.value })}
                    placeholder="Min 6 characters" show={showNew}
                    onToggle={() => setShowNew(!showNew)} testId="new-password-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Confirm New Password</Label>
                  <PasswordInput
                    id="confirmPassword" value={form.confirmPassword}
                    onChange={(e) => setForm({ ...form, confirmPassword: e.target.value })}
                    placeholder="Repeat new password" show={showConfirm}
                    onToggle={() => setShowConfirm(!showConfirm)} testId="confirm-password-input"
                  />
                </div>
                <Button type="submit" disabled={isLoading} className="w-full bg-slate-900 hover:bg-slate-800" data-testid="change-password-submit">
                  {isLoading ? (<><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Changing...</>) : 'Set New Password'}
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default ChangePasswordPage;
